import { apiFetch } from "@/api/client";
import { ApiError } from "@/api/errors";
import type {
  AnalysisVersionResponse,
  MeetingCard,
  MeetingMessage,
  MeetingPrecheckRequest,
  MeetingPrecheckResponse,
  MeetingRerunRequest,
  MeetingTimelineResponse,
  UUID,
} from "@/api/types";

/** POST /api/meetings/precheck — hash-based dedup probe. */
export function precheckMeeting(
  payload: MeetingPrecheckRequest,
): Promise<MeetingPrecheckResponse> {
  return apiFetch<MeetingPrecheckResponse>("/api/meetings/precheck", {
    method: "POST",
    body: payload,
  });
}

export interface CreateMeetingFields {
  client_id: UUID;
  meeting_type_id: UUID;
  participants: UUID[];
  force_new?: boolean;
  /** Optional SHA-256 hint; backend re-hashes server-side and rejects on mismatch. */
  sha256_hint?: string;
  /**
   * Owning folder (folder-management-v3 §2.3). Required for non-admin
   * callers; omitted → BE falls back to active-folder for admin / 400
   * for non-admin (`require_explicit_for_non_admin=True`).
   */
  folder_id?: UUID;
}

export interface CreateMeetingResult {
  id: UUID;
}

export interface UploadMeetingOptions {
  signal?: AbortSignal;
  onProgress?: (loaded: number, total: number) => void;
}

/**
 * Multipart upload via XHR — `fetch` does not expose request-progress events.
 * Returns the newly created `Meeting.id`. Network failures and non-2xx
 * responses surface as `ApiError`, matching the rest of the API layer.
 */
export function uploadMeeting(
  file: File,
  fields: CreateMeetingFields,
  options: UploadMeetingOptions = {},
): Promise<CreateMeetingResult> {
  return new Promise<CreateMeetingResult>((resolve, reject) => {
    // Backend (POST /api/meetings) expects two multipart fields:
    //   * `file`     — the upload bytes
    //   * `metadata` — a JSON-encoded MeetingUploadMetadata
    const metadata: Record<string, unknown> = {
      client_id: fields.client_id,
      meeting_type_id: fields.meeting_type_id,
      participants: fields.participants,
    };
    if (fields.force_new === true) metadata.force_new = true;
    if (fields.sha256_hint) metadata.sha256_hint = fields.sha256_hint;
    if (fields.folder_id !== undefined) metadata.folder_id = fields.folder_id;

    const form = new FormData();
    form.set("file", file, file.name);
    form.set("metadata", JSON.stringify(metadata));

    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/meetings");
    xhr.withCredentials = true;
    xhr.responseType = "json";

    if (options.onProgress) {
      const cb = options.onProgress;
      xhr.upload.onprogress = (evt: ProgressEvent) => {
        if (evt.lengthComputable) cb(evt.loaded, evt.total);
      };
    }
    options.signal?.addEventListener("abort", () => xhr.abort());

    xhr.onerror = () =>
      reject(
        new ApiError({
          code: "network_error",
          message: "Network request failed",
          status: 0,
        }),
      );
    xhr.onabort = () =>
      reject(
        new ApiError({
          code: "aborted",
          message: "Upload aborted",
          status: 0,
        }),
      );
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const body = xhr.response as CreateMeetingResult | null;
        if (body !== null && typeof body.id === "string") {
          resolve(body);
          return;
        }
        reject(
          new ApiError({
            code: "invalid_response",
            message: "Upload succeeded but response body was malformed",
            status: xhr.status,
          }),
        );
        return;
      }
      const envelope =
        (xhr.response as { code?: string; message?: string } | null) ?? {};
      reject(
        new ApiError({
          code: envelope.code ?? `http_${xhr.status}`,
          message: envelope.message ?? xhr.statusText,
          status: xhr.status,
        }),
      );
    };

    xhr.send(form);
  });
}

/** GET /api/meetings/:id — full card payload with versions + transcript. */
export function getMeetingCard(id: UUID): Promise<MeetingCard> {
  return apiFetch<MeetingCard>(`/api/meetings/${id}`, { method: "GET" });
}

/**
 * POST /api/meetings/:id/rerun — creates a fresh AnalysisVersion.
 *
 * TM-fe04aa2b: an optional body may carry `custom_prompt_text` for a
 * one-off override. When `body` is omitted or `custom_prompt_text` is
 * empty the BE falls back to the active prompt of the meeting type
 * (legacy contract preserved).
 */
export function rerunMeeting(
  id: UUID,
  body?: MeetingRerunRequest,
): Promise<AnalysisVersionResponse> {
  return apiFetch<AnalysisVersionResponse>(`/api/meetings/${id}/rerun`, {
    method: "POST",
    body: body ?? undefined,
  });
}

/** POST /api/analysis_versions/:id/retry-consensus — phase 5 only on partial. */
export function retryConsensus(
  analysisVersionId: UUID,
): Promise<AnalysisVersionResponse> {
  return apiFetch<AnalysisVersionResponse>(
    `/api/analysis_versions/${analysisVersionId}/retry-consensus`,
    { method: "POST" },
  );
}

/**
 * POST /api/meetings/:id/resume — ADR-015.
 *
 * Resume the latest AnalysisVersion from its failed phase, reusing the
 * artifacts the orchestrator already stashed (transcript text, A/B/C
 * candidates). The backend decides which phase to restart from based on
 * ``partial_results.phase_artifacts``; the caller only knows that the
 * action is "do the cheap thing".
 *
 * Returns 409 when no artifacts are reusable — callers should surface
 * that as a "use /rerun instead" prompt, not silently swallow it.
 */
export function resumeMeeting(id: UUID): Promise<AnalysisVersionResponse> {
  return apiFetch<AnalysisVersionResponse>(`/api/meetings/${id}/resume`, {
    method: "POST",
  });
}

/** GET /api/meetings/:id/timeline — per-phase event log for the 5-step indicator. */
export function getMeetingTimeline(id: UUID): Promise<MeetingTimelineResponse> {
  return apiFetch<MeetingTimelineResponse>(`/api/meetings/${id}/timeline`, {
    method: "GET",
  });
}

/**
 * PATCH /api/meetings/:id/client — rebind a meeting to a different client.
 *
 * TaskMe 8760a3e1 (revisit) — Dmitri simplified: client is picked manually
 * on upload and editable from the card. The endpoint replaces
 * ``Meeting.client_id`` with the registry ``client_id`` in the body,
 * returning the full ``MeetingCard`` so the FE can swap its react-query
 * cache wholesale (same envelope contract as the matching endpoints).
 *
 * Authorization (FR-25): admin/manager any meeting, user only «свои».
 * Backend 400s on a non-existent target client.
 */
export function updateMeetingClient(
  id: UUID,
  clientId: UUID,
): Promise<MeetingCard> {
  return apiFetch<MeetingCard>(`/api/meetings/${id}/client`, {
    method: "PATCH",
    body: { client_id: clientId },
  });
}

// ---- Chat (TM-d6d6bf78 + TM-2f2aa823) -----------------------------------

/** GET /api/meetings/:id/messages — full chat thread, ASC by created_at. */
export function listMeetingMessages(id: UUID): Promise<MeetingMessage[]> {
  return apiFetch<MeetingMessage[]>(`/api/meetings/${id}/messages`, {
    method: "GET",
  });
}

/** POST /api/meetings/:id/messages — write a user message (no LLM). */
export function postMeetingMessage(
  id: UUID,
  text: string,
): Promise<MeetingMessage> {
  return apiFetch<MeetingMessage>(`/api/meetings/${id}/messages`, {
    method: "POST",
    body: { text },
  });
}

/** Final payload of the `done` SSE event on `/messages/ask`. */
export interface AskLLMResult {
  id: UUID;
  text: string;
  cost_usd: string;
  input_tokens: number;
  output_tokens: number;
  cache_hit_pct: number;
  context_mode: string;
}

/** Options for {@link askLLM} — pluggable fetch + onChunk callback. */
export interface AskLLMOptions {
  onChunk?: (delta: string) => void;
  signal?: AbortSignal;
  /** Inject a custom fetch (used in tests). Same shape as the global one. */
  fetchImpl?: typeof fetch;
}

/**
 * POST /api/meetings/:id/messages/ask — fire an LLM-Q&A, stream the answer.
 *
 * Resolves with the final {@link AskLLMResult} once the `done` SSE event
 * lands. Each `chunk` event triggers `onChunk(delta)` so the UI can grow
 * the assistant bubble in real time. Errors (network, server-emitted
 * `error` SSE event) reject the promise so callers can surface a toast.
 */
export async function askLLM(
  id: UUID,
  text: string,
  options: AskLLMOptions = {},
): Promise<AskLLMResult> {
  const f = options.fetchImpl ?? fetch;
  const resp = await f(`/api/meetings/${id}/messages/ask`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    body: JSON.stringify({ text }),
    signal: options.signal,
  });
  if (!resp.ok || resp.body === null) {
    const envelope = (await safeJson(resp)) as {
      code?: string;
      message?: string;
    } | null;
    throw new ApiError({
      code: envelope?.code ?? `http_${resp.status}`,
      message: envelope?.message ?? resp.statusText,
      status: resp.status,
    });
  }
  return await parseAskStream(resp.body, options.onChunk);
}

async function safeJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Minimal SSE parser: splits on blank-line frame boundaries, extracts
 * `event:` / `data:` fields. Multi-line `data:` are joined with `\n`.
 * Resolves on `done`; throws on `error` event or stream end without `done`.
 */
async function parseAskStream(
  body: ReadableStream<Uint8Array>,
  onChunk: ((delta: string) => void) | undefined,
): Promise<AskLLMResult> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let done: AskLLMResult | null = null;
  while (done === null) {
    const { value, done: streamDone } = await reader.read();
    if (streamDone) break;
    buffer += decoder.decode(value, { stream: true });
    let sepIndex = buffer.indexOf("\n\n");
    while (sepIndex !== -1) {
      const frame = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex + 2);
      const parsed = parseSseFrame(frame);
      if (parsed === null) {
        sepIndex = buffer.indexOf("\n\n");
        continue;
      }
      if (parsed.event === "chunk") {
        const delta = parsed.data?.delta;
        if (typeof delta === "string" && onChunk !== undefined) onChunk(delta);
      } else if (parsed.event === "done") {
        done = parsed.data as unknown as AskLLMResult;
      } else if (parsed.event === "error") {
        const payload = parsed.data ?? {};
        throw new ApiError({
          code:
            typeof payload.code === "string"
              ? (payload.code as string)
              : "chat_llm_error",
          message:
            typeof payload.message === "string"
              ? (payload.message as string)
              : "chat stream error",
          status: 502,
        });
      }
      sepIndex = buffer.indexOf("\n\n");
    }
  }
  if (done === null) {
    throw new ApiError({
      code: "chat_stream_truncated",
      message: "Stream ended without a `done` event",
      status: 502,
    });
  }
  return done;
}

interface ParsedFrame {
  event: string;
  data: Record<string, unknown> | null;
}

function parseSseFrame(frame: string): ParsedFrame | null {
  if (frame.trim() === "") return null;
  let event = "message";
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trim());
    }
  }
  if (dataLines.length === 0) return { event, data: null };
  const raw = dataLines.join("\n");
  try {
    return { event, data: JSON.parse(raw) as Record<string, unknown> };
  } catch {
    return { event, data: null };
  }
}

/**
 * POST /api/meetings/:id/participants/add — matching-v2 §7.2.
 *
 * Appends ``employee_id`` to ``meetings.participants[]``. Idempotent
 * on the backend: posting a duplicate is a 200 no-op. Returns the
 * full ``MeetingCard`` so callers can swap their react-query cache
 * wholesale instead of issuing a refetch.
 *
 * TM 9e46885b moved this helper out of the inline `apiFetch` callsite
 * in `MeetingCardView` so the new «Участники» tab UI can share it
 * with the transcript-mention chip.
 */
export function addMeetingParticipant(
  meetingId: UUID,
  employeeId: UUID,
): Promise<MeetingCard> {
  return apiFetch<MeetingCard>(`/api/meetings/${meetingId}/participants/add`, {
    method: "POST",
    body: { employee_id: employeeId },
  });
}

/**
 * POST /api/meetings/:id/participants/remove — TM 9e46885b.
 *
 * Mirror of :func:`addMeetingParticipant` for the «×» affordance in
 * the meeting-card «Участники» tab. Idempotent on the backend: posting
 * an id that is not in the list is a 200 no-op (covers the racy
 * «already removed» case when two operators edit the same card).
 * Returns the refreshed ``MeetingCard``.
 */
export function removeMeetingParticipant(
  meetingId: UUID,
  employeeId: UUID,
): Promise<MeetingCard> {
  return apiFetch<MeetingCard>(
    `/api/meetings/${meetingId}/participants/remove`,
    {
      method: "POST",
      body: { employee_id: employeeId },
    },
  );
}
